import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

const Amount = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [redirectCountdown, setRedirectCountdown] = useState(5);
    const { t, i18n } = useTranslation();

    const id = window.location.pathname.split("/").pop();

    const handlePayment = async () => {
        try {
            // Map current language to API language codes
            const currentLang = i18n.language.toLowerCase();
            let apiLanguage = 'EN'; // Default to EN

            if (currentLang === 'ka' || currentLang === 'ge') {
                apiLanguage = 'KA';
            } else if (currentLang === 'en' || currentLang === 'ru') {
                apiLanguage = 'EN';
            }

            const response = await fetch('https://api.geoglobalgate.com/api/pay/initiate', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-secret': '6392e6808fb9d86e13a0e2fe341f8ad8'
                },
                body: JSON.stringify({
                    request_id: id,
                    amount: data.amount,
                    currency: 'GEL',
                    language: apiLanguage,
                    description: `Payment for car ${data.car_number}`
                })
            });

            const result = await response.json();
            console.log('Payment Response:', result);

            if (result.payment_url && result.params) {
                // შექმენი ფორმა POST-ით
                const form = document.createElement("form");
                form.method = "POST";
                form.action = result.payment_url;

                // დაამატე params როგორც hidden inputs
                for (const key in result.params) {
                    if (result.params.hasOwnProperty(key)) {
                        const input = document.createElement("input");
                        input.type = "hidden";
                        input.name = key;
                        input.value = result.params[key];
                        form.appendChild(input);
                    }
                }

                document.body.appendChild(form);
                form.submit();
            } else {
                alert('Payment URL or parameters not found in response.');
            }
        } catch (error) {
            console.error('Payment Error:', error);
            alert('გადახდის შეცდომა. გთხოვთ სცადოთ თავიდან.');
        }
    };

    useEffect(() => {
        const fetchAmount = async () => {
            try {
                const response = await fetch(`https://api.geoglobalgate.com/api/amount/${id}`);
                const result = await response.json();
                setData(result);
            } catch (error) {
                console.error("მონაცემების წამოღების შეცდომა:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchAmount();
    }, [id]);

    // Redirect countdown effect
    useEffect(() => {
        if (!data && !loading) {
            const timer = setInterval(() => {
                setRedirectCountdown((prev) => {
                    if (prev <= 1) {
                        // Redirect to main page
                        window.location.href = "/";
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);

            return () => clearInterval(timer);
        }
    }, [data, loading]);

    if (loading) {
        return (
            <div style={styles.container}>
                <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
                    style={{ fontSize: "18px", color: "#fff" }}
                >
                    იტვირთება...
                </motion.div>
            </div>
        );
    }

    if (!data) {
        return (
            <div style={styles.container}>
                <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, type: "spring" }}
                    style={styles.notFoundCard}
                >
                    {/* Error Icon Animation */}
                    <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: 0.2, duration: 0.5, type: "spring", stiffness: 200 }}
                        style={styles.iconContainer}
                    >
                        <motion.div
                            animate={{ 
                                rotate: [0, -10, 10, -10, 10, 0],
                            }}
                            transition={{ 
                                delay: 0.7,
                                duration: 0.5,
                                ease: "easeInOut"
                            }}
                            style={styles.errorIcon}
                        >
                            ⚠️
                        </motion.div>
                    </motion.div>

                    {/* Error Message */}
                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4, duration: 0.5 }}
                        style={styles.notFoundTitle}
                    >
                        მონაცემები ვერ მოიძებნა
                    </motion.h2>

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.5, duration: 0.5 }}
                        style={styles.notFoundDescription}
                    >
                        სამწუხაროდ, მოთხოვნილი ინფორმაცია ვერ მოიძებნა
                    </motion.p>

                    {/* Redirect Countdown */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.6, duration: 0.5 }}
                        style={styles.redirectInfo}
                    >
                        <p style={styles.redirectText}>
                            მთავარ გვერდზე გადამისამართება {redirectCountdown} წამში...
                        </p>
                        
                        {/* Progress Bar */}
                        <div style={styles.progressBarContainer}>
                            <motion.div
                                initial={{ width: "100%" }}
                                animate={{ width: "0%" }}
                                transition={{ duration: 5, ease: "linear" }}
                                style={styles.progressBar}
                            />
                        </div>
                    </motion.div>

                    {/* Manual Return Button */}
                    <motion.button
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.7, duration: 0.5 }}
                        whileHover={{ scale: 1.05, boxShadow: "0 6px 20px rgba(108, 92, 231, 0.4)" }}
                        whileTap={{ scale: 0.95 }}
                        style={styles.returnButton}
                        onClick={() => {
                            window.location.href = "/";
                        }}
                    >
                        <span style={styles.buttonIcon}>🏠</span>
                        მთავარ გვერდზე დაბრუნება
                    </motion.button>
                </motion.div>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                style={styles.card}
            >
                <h2 style={styles.title}>{t("paymentAmount")}</h2>
                <p style={styles.amount}>{data.amount} ₾</p>

                <motion.div
                    initial={{ opacity: 0, x: -50 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5 }}
                    whileHover={{ scale: 1.05, backgroundColor: "#eae4ff" }}
                    style={styles.carNumberBox}
                >
                    <span style={styles.carLabel}>{t("carNumber")}:</span>
                    <motion.span
                        whileHover={{ color: "#8e44ad" }}
                        transition={{ duration: 0.3 }}
                        style={styles.carNumber}
                    >
                        {data.car_number}
                    </motion.span>
                </motion.div>

                <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    style={styles.button}
                    onClick={handlePayment}
                >
                    💳 {t("payButton")}
                </motion.button>
            </motion.div>
        </div>
    );
};

const styles = {
    container: {
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        background: "linear-gradient(135deg, #8e44ad, #6c5ce7)",
        fontFamily: "sans-serif",
        padding: "20px",
    },
    card: {
        background: "#fff",
        padding: "30px 40px",
        borderRadius: "16px",
        boxShadow: "0 8px 20px rgba(0,0,0,0.15)",
        textAlign: "center",
        width: "320px",
    },
    title: {
        marginBottom: "10px",
        fontSize: "20px",
        color: "#6c5ce7",
    },
    amount: {
        fontSize: "26px",
        fontWeight: "bold",
        marginBottom: "20px",
        color: "#2d3436",
    },
    carNumberBox: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        background: "#f3f0ff",
        padding: "10px 15px",
        borderRadius: "8px",
        marginBottom: "20px",
        fontSize: "16px",
        fontWeight: "500",
        border: "1px solid #dcd6f7",
        cursor: "pointer",
    },
    carLabel: {
        color: "#6c5ce7",
    },
    carNumber: {
        color: "#2d3436",
        fontWeight: "bold",
    },
    button: {
        background: "linear-gradient(135deg, #8e44ad, #6c5ce7)",
        color: "#fff",
        padding: "12px 20px",
        border: "none",
        borderRadius: "8px",
        cursor: "pointer",
        fontSize: "16px",
        width: "100%",
        transition: "0.3s",
    },
    // Not Found Styles
    notFoundCard: {
        background: "#fff",
        padding: "40px",
        borderRadius: "20px",
        boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
        textAlign: "center",
        maxWidth: "400px",
        width: "100%",
    },
    iconContainer: {
        display: "flex",
        justifyContent: "center",
        marginBottom: "20px",
    },
    errorIcon: {
        fontSize: "64px",
        display: "inline-block",
    },
    notFoundTitle: {
        fontSize: "24px",
        color: "#2d3436",
        marginBottom: "12px",
        fontWeight: "bold",
    },
    notFoundDescription: {
        fontSize: "16px",
        color: "#636e72",
        marginBottom: "30px",
        lineHeight: "1.5",
    },
    redirectInfo: {
        marginBottom: "20px",
    },
    redirectText: {
        fontSize: "14px",
        color: "#b2bec3",
        marginBottom: "10px",
    },
    progressBarContainer: {
        width: "100%",
        height: "4px",
        background: "#f0f0f0",
        borderRadius: "2px",
        overflow: "hidden",
        marginBottom: "20px",
    },
    progressBar: {
        height: "100%",
        background: "linear-gradient(135deg, #8e44ad, #6c5ce7)",
        borderRadius: "2px",
    },
    returnButton: {
        background: "linear-gradient(135deg, #6c5ce7, #8e44ad)",
        color: "#fff",
        padding: "14px 28px",
        border: "none",
        borderRadius: "10px",
        cursor: "pointer",
        fontSize: "16px",
        fontWeight: "600",
        display: "inline-flex",
        alignItems: "center",
        gap: "8px",
        transition: "all 0.3s ease",
        boxShadow: "0 4px 15px rgba(108, 92, 231, 0.3)",
    },
    buttonIcon: {
        fontSize: "20px",
    },
};

export default Amount;